// src/components/Login.js (Updated with JWT and Company Info)

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    IconButton,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    alpha,
    CircularProgress
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Login as LoginIcon,
    Lock as LockIcon,
    Email as EmailIcon,
    HealthAndSafety
} from '@mui/icons-material';
import useCustomToast from '../hooks/useToast';
import { companyInfo as defaultCompanyInfo } from '../utils/dataInfo';
import { authService } from '../apiServices/authService';
import { companyService } from '../apiServices/companyService';
import { motion } from 'framer-motion';

const Login = () => {
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        showPassword: false
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({
        email: '',
        password: ''
    });

    // State for company info from backend
    const [companyInfoState, setCompanyInfoState] = useState({
        name: defaultCompanyInfo.name || 'Rental Collection  Management System',
        logo_url: defaultCompanyInfo.logo || null,
        has_logo: false,
        code: null,
        currency: 'UGX'
    });
    const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(true);
    const [logoError, setLogoError] = useState(false);

    // Check if already logged in
    useEffect(() => {
        if (authService.isAuthenticated()) {
            navigate('/properties');
        }
    }, [navigate]);

    // Fetch company info on component mount
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            setLoadingCompanyInfo(true);
            try {
                const branchId = localStorage.getItem('branch_id') || null;
                const response = await companyService.getLoginInfo(branchId);
                
                if (response.status === 'success' && response.data) {
                    setCompanyInfoState({
                        name: response.data.name || defaultCompanyInfo.name || 'Rental Collection  Management System',
                        logo_url: response.data.logo_url || defaultCompanyInfo.logo || null,
                        has_logo: response.data.has_logo || false,
                        code: response.data.code,
                        currency: response.data.currency || 'UGX'
                    });
                } else {
                    // Fallback to default company info
                    setCompanyInfoState({
                        name: defaultCompanyInfo.name || 'Rental Collection  Management System',
                        logo_url: defaultCompanyInfo.logo || null,
                        has_logo: !!defaultCompanyInfo.logo,
                        code: null,
                        currency: 'UGX'
                    });
                }
            } catch (error) {
                console.error('Failed to fetch company info:', error);
                // Keep default values on error
                setCompanyInfoState({
                    name: defaultCompanyInfo.name || 'Rental Collection  Management System',
                    logo_url: defaultCompanyInfo.logo || null,
                    has_logo: !!defaultCompanyInfo.logo,
                    code: null,
                    currency: 'UGX'
                });
            } finally {
                setLoadingCompanyInfo(false);
            }
        };
        
        fetchCompanyInfo();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const togglePasswordVisibility = () => {
        setFormData(prev => ({
            ...prev,
            showPassword: !prev.showPassword
        }));
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            email: '',
            password: ''
        };

        if (!formData.email) {
            newErrors.email = 'Email is required';
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
            valid = false;
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleLogin = async () => {
        if (!validateForm()) {
            showToast("Warning", "Please fill in all required fields", "warning");
            return;
        }

        setLoading(true);

        try {
            const result = await authService.login(formData.email, formData.password);

            if (result.success) {
                showToast("Success", "Login successful", "success");
                navigate("/dashboard");
            } else {
                showToast("Error", result.message || "Login failed", "error");
            }
        } catch (error) {
            showToast("Error", "An unexpected error occurred", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleLogin();
        }
    };

    const handleLogoError = () => {
        setLogoError(true);
    };

    // Show loading state while fetching company info
    if (loadingCompanyInfo) {
        return (
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}>
                <CircularProgress size={60} sx={{ color: 'white' }} />
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            position: 'relative',
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
            p: isMobile ? 2 : 3,
        }}>
            {/* Background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.2) 100%)',
                    }
                }}
            />

            {/* Main Login Card */}
            <Fade in={true} timeout={800}>
                <Paper 
                    elevation={0}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        width: '100%',
                        maxWidth: isMobile ? '92%' : isTablet ? 420 : 480,
                        p: isMobile ? 3 : { xs: 4, sm: 5 },
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.background.paper, 0.85),
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                    }}
                >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Zoom in={true} timeout={500}>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                {/* Company Logo - Now from backend */}
                                {(companyInfoState.has_logo && companyInfoState.logo_url && !logoError) ? (
                                    <motion.div
                                        initial={{ scale: 0.9, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mb: 2,
                                            position: 'relative'
                                        }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: isMobile ? 100 : 120,
                                                height: isMobile ? 100 : 120,
                                                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 70%)`,
                                                borderRadius: '50%',
                                                filter: 'blur(20px)',
                                                zIndex: 0
                                            }} />
                                            <img
                                                src={companyInfoState.logo_url}
                                                alt={`${companyInfoState.name} Logo`}
                                                style={{
                                                    height: isMobile ? '70px' : '90px',
                                                    width: 'auto',
                                                    maxWidth: '100%',
                                                    objectFit: 'contain',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))'
                                                }}
                                                onError={handleLogoError}
                                            />
                                        </Box>
                                    </motion.div>
                                ) : (
                                    // Fallback: Show company initial or default logo
                                    <motion.div
                                        initial={{ scale: 0.9, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mb: 2,
                                            position: 'relative'
                                        }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: isMobile ? 100 : 120,
                                                height: isMobile ? 100 : 120,
                                                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 70%)`,
                                                borderRadius: '50%',
                                                filter: 'blur(20px)',
                                                zIndex: 0
                                            }} />
                                            <Box sx={{
                                                height: isMobile ? '70px' : '90px',
                                                width: isMobile ? '70px' : '90px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                borderRadius: '20px',
                                                position: 'relative',
                                                zIndex: 1,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }}>
                                                <Typography
                                                    variant={isMobile ? 'h3' : 'h2'}
                                                    sx={{
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    {companyInfoState.name.charAt(0)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </motion.div>
                                )}

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                >
                                    <Typography
                                        variant={isMobile ? 'h5' : 'h5'}
                                        component="h5"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1,
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            letterSpacing: '-0.5px'
                                        }}
                                    >
                                        Welcome Back !
                                    </Typography>
                                </motion.div>

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: 'text.secondary',
                                            mt: 1,
                                            fontWeight: 500,
                                            fontSize: isMobile ? '0.85rem' : '0.95rem'
                                        }}
                                    >
                                        Log in to Contuine
                                    </Typography>
                                </motion.div>
                            </Box>
                        </Zoom>

                        <motion.div
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <Box component="form" sx={{ mt: 3 }} onKeyPress={handleKeyPress}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    margin="normal"
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: theme.palette.primary.main }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.common.white, 0.7),
                                            backdropFilter: 'blur(4px)',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.common.white, 0.9),
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: alpha(theme.palette.common.white, 0.95),
                                                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                                            }
                                        }
                                    }}
                                    size={isMobile ? 'small' : 'medium'}
                                />

                                <TextField
                                    fullWidth
                                    label="Password"
                                    name="password"
                                    type={formData.showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    margin="normal"
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ color: theme.palette.primary.main }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={togglePasswordVisibility}
                                                    edge="end"
                                                    size={isMobile ? 'small' : 'medium'}
                                                    disabled={loading}
                                                >
                                                    {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 1,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.common.white, 0.7),
                                            backdropFilter: 'blur(4px)',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.common.white, 0.9),
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: alpha(theme.palette.common.white, 0.95),
                                                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                                            }
                                        }
                                    }}
                                    size={isMobile ? 'small' : 'medium'}
                                />

                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    mb: 3
                                }}>
                                    <Link
                                        to="/forget-password"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontWeight: 600,
                                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                '&:hover': {
                                                    textDecoration: 'underline'
                                                }
                                            }}
                                        >
                                            Forgot password?
                                        </Typography>
                                    </Link>
                                </Box>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={handleLogin}
                                    disabled={loading}
                                    startIcon={!loading && <LoginIcon />}
                                    sx={{
                                        py: isMobile ? 1.2 : 1.5,
                                        mb: 2,
                                        fontSize: isMobile ? '0.9rem' : '1rem',
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`
                                        },
                                        '&:active': {
                                            transform: 'translateY(0)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    size={isMobile ? 'medium' : 'large'}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Log In'}
                                </Button>

                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                                }}>
                                    <HealthAndSafety sx={{ fontSize: 14, mr: 0.5, color: theme.palette.primary.main }} />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: isMobile ? '0.65rem' : '0.7rem',
                                            fontWeight: 600
                                        }}
                                    >
                                        {companyInfoState.name}
                                    </Typography>
                                </Box>
                            </Box>
                        </motion.div>
                    </Box>
                </Paper>
            </Fade>
        </Box>
    );
};

export default Login;